import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { usePrintoutPresetsDefaultImpact } from "../../hooks/usePrintoutPresets";

type Props = {
	/** The dialog is showing: the count of people who'd lose their saved printouts is looked up only then. */
	open: boolean;
};

type ImpactState = "loading" | "failed" | "none" | "some";

/**
 * What Save as default does from the printout popup, every consequence spelled out: the department's
 * printouts are replaced for everyone, the people with saved printouts of their own lose them (counted
 * when the dialog opens), and everyone — the saver too — starts on Printout 1. If the count can't be
 * loaded the save can still go ahead; the dialog says so rather than guessing a number.
 */
export const PrintoutSaveAsDefaultNotice: FC<Props> = ({ open }) => {
	const { t } = useTranslation();
	const impact = usePrintoutPresetsDefaultImpact(open);

	const { state, line } = ((): { state: ImpactState; line: string } => {
		if (impact.isError) return { state: "failed", line: t("printout.save.as.default.body.own.failed") };
		if (!impact.data) return { state: "loading", line: t("printout.save.as.default.body.own.loading") };
		const count = impact.data.usersWithOwnPresets;
		if (count === 0) return { state: "none", line: t("printout.save.as.default.body.own.none") };
		return { state: "some", line: t("printout.save.as.default.body.own.count", { count }) };
	})();

	return (
		<Box data-testid="printout-save-as-default-notice" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<Typography color="text.secondary">{t("printout.save.as.default.body.replace")}</Typography>
			<Typography color="text.secondary" role="status" aria-live="polite" data-testid="printout-save-as-default-impact" data-state={state}>
				{line}
			</Typography>
			<Typography color="text.secondary">{t("printout.save.as.default.body.printout1")}</Typography>
		</Box>
	);
};
